import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, remove } from "firebase/database";
import { app } from "./firebase/config";
import "./AdminUserManagement.css";

const AdminErrorManagement = () => {
  const [errors, setErrors] = useState([]);
  const [filteredErrors, setFilteredErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorsPerPage] = useState(10);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({
    totalErrors: 0,
    criticalErrors: 0,
    warningErrors: 0,
    infoErrors: 0,
  });
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Toast notification function
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // Calculate statistics
  const calculateStats = (errorsData) => {
    let totalErrors = errorsData.length;
    let criticalErrors = 0;
    let warningErrors = 0;
    let infoErrors = 0;

    errorsData.forEach((error) => {
      switch (error.errorType) {
        case "Error":
          criticalErrors++;
          break;
        case "Warning":
          warningErrors++;
          break;
        case "Info":
          infoErrors++;
          break;
        default:
          break;
      }
    });

    setStats({
      totalErrors,
      criticalErrors,
      warningErrors,
      infoErrors,
    });
  };

  // Lấy dữ liệu lỗi từ Firebase
  useEffect(() => {
    const fetchErrors = async () => {
      setLoading(true);
      try {
        const db = getDatabase(app);
        const errorsRef = ref(db, "errors");
        const snapshot = await get(errorsRef);

        const errorsData = snapshot.val();
        if (errorsData) {
          const errorsList = Object.entries(errorsData)
            .map(([id, data]) => ({
              id,
              ...data,
            }))
            .sort((a, b) => {
              // Phân tích timestamp ISO 8601 và xử lý lỗi
              const dateA = a.timestamp ? new Date(a.timestamp) : null;
              const dateB = b.timestamp ? new Date(b.timestamp) : null;

              const isValidA = dateA && !isNaN(dateA.getTime());
              const isValidB = dateB && !isNaN(dateB.getTime());

              if (isValidA && isValidB) {
                // Cả hai đều hợp lệ, sắp xếp giảm dần (mới nhất trước)
                return dateB.getTime() - dateA.getTime();
              } else if (isValidA) {
                // Chỉ A hợp lệ, A đứng trước B (đẩy B xuống cuối)
                return -1;
              } else if (isValidB) {
                // Chỉ B hợp lệ, B đứng trước A (đẩy A xuống cuối)
                return 1;
              } else {
                // Cả hai đều không hợp lệ, giữ nguyên thứ tự tương đối
                return 0;
              }
            });
          setErrors(errorsList);
          setFilteredErrors(errorsList);
          calculateStats(errorsList);
          showToast("Dữ liệu lỗi đã được cập nhật!");
        } else {
          setErrors([]);
          setFilteredErrors([]);
          setStats({
            totalErrors: 0,
            criticalErrors: 0,
            warningErrors: 0,
            infoErrors: 0,
          });
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu lỗi:", error);
        setErrors([]);
        setFilteredErrors([]);
        showToast("Lỗi khi tải dữ liệu: " + error.message, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchErrors();
  }, []);

  // Lọc lỗi theo từ khóa tìm kiếm và loại lỗi
  useEffect(() => {
    let results = errors;

    // Lọc theo loại lỗi
    if (filter !== "all") {
      results = results.filter((error) => error.errorType === filter);
    }

    // Lọc theo từ khóa tìm kiếm
    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      results = results.filter((error) => {
        return (
          (error.message &&
            error.message.toLowerCase().includes(searchLower)) ||
          (error.errorCode &&
            error.errorCode.toLowerCase().includes(searchLower)) ||
          (error.errorLocation &&
            error.errorLocation.toLowerCase().includes(searchLower)) ||
          (error.id && error.id.toLowerCase().includes(searchLower))
        );
      });
    }

    setFilteredErrors(results);
    setCurrentPage(1);
  }, [searchText, filter, errors]);

  // Xử lý hiển thị chi tiết lỗi
  const handleErrorClick = (error) => {
    setCurrentError(error);
    setShowErrorModal(true);
  };

  // Xử lý xóa lỗi
  const handleDeleteError = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa lỗi này?")) {
      return;
    }

    try {
      const db = getDatabase(app);
      const errorRef = ref(db, `errors/${currentError.id}`);

      await remove(errorRef);
      setShowErrorModal(false);

      // Cập nhật state
      const updatedErrors = errors.filter(
        (error) => error.id !== currentError.id
      );
      setErrors(updatedErrors);
      calculateStats(updatedErrors);
      showToast("Đã xóa lỗi thành công!");
    } catch (error) {
      console.error("Lỗi khi xóa thông tin lỗi:", error);
      showToast("Có lỗi khi xóa lỗi: " + error.message, "error");
    }
  };

  // Xử lý xóa tất cả lỗi
  const handleDeleteAllErrors = async () => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa tất cả lỗi?")) {
      return;
    }

    try {
      const db = getDatabase(app);
      const errorsRef = ref(db, "errors");

      await remove(errorsRef);

      // Cập nhật state
      setErrors([]);
      setFilteredErrors([]);
      setStats({
        totalErrors: 0,
        criticalErrors: 0,
        warningErrors: 0,
        infoErrors: 0,
      });
      showToast("Đã xóa tất cả lỗi thành công!");
    } catch (error) {
      console.error("Lỗi khi xóa tất cả lỗi:", error);
      showToast("Có lỗi khi xóa tất cả lỗi: " + error.message, "error");
    }
  };

  // Lấy danh sách các loại lỗi duy nhất
  const getUniqueErrorTypes = () => {
    const errorTypes = new Set(
      errors
        .map((error) => error.errorType)
        .filter((type) => type !== undefined && type !== "all")
    );
    return ["all", ...Array.from(errorTypes)];
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchText("");
    setFilter("all");
    setCurrentPage(1);
  };

  // Định dạng hiển thị mức độ nghiêm trọng của lỗi
  const getErrorSeverityClass = (error) => {
    switch (error.errorType) {
      case "Error":
        return "inactive";
      case "Warning":
        return "warning";
      case "Info":
        return "success";
      case "Debug":
        return "pending";
      default:
        return "pending";
    }
  };

  const getErrorSeverityText = (error) => {
    switch (error.errorType) {
      case "Error":
        return "Nghiêm trọng";
      case "Warning":
        return "Cảnh báo";
      case "Info":
        return "Thông tin";
      case "Debug":
        return "Debug";
      default:
        return "Không xác định";
    }
  };

  // Định dạng thời gian
  const formatDate = (timestamp) => {
    if (!timestamp) return "Không xác định";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        console.error("Timestamp không hợp lệ:", timestamp);
        return "Thời gian không hợp lệ";
      }

      return date.toLocaleString("vi-VN", {
        timeZone: "Asia/Saigon",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      console.error(
        "Lỗi khi định dạng thời gian:",
        error,
        "Timestamp:",
        timestamp
      );
      return "Lỗi định dạng";
    }
  };

  // Close modal
  const closeErrorModal = () => {
    setShowErrorModal(false);
    setCurrentError(null);
  };

  // Pagination
  const indexOfLastError = currentPage * errorsPerPage;
  const indexOfFirstError = indexOfLastError - errorsPerPage;
  const currentErrors = filteredErrors.slice(
    indexOfFirstError,
    indexOfLastError
  );
  const totalPages = Math.ceil(filteredErrors.length / errorsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Hiển thị {indexOfFirstError + 1}-
          {Math.min(indexOfLastError, filteredErrors.length)} của{" "}
          {filteredErrors.length} lỗi
        </div>
        <div className="pagination-buttons">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Trước
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="pagination-button"
              >
                1
              </button>
              {startPage > 2 && <span className="pagination-dots">...</span>}
            </>
          )}

          {pageNumbers.map((number) => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`pagination-button ${
                currentPage === number ? "active" : ""
              }`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="pagination-dots">...</span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="pagination-button"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Sau
          </button>
        </div>
      </div>
    );
  };

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="skeleton-container">
      {[...Array(5)].map((_, index) => (
        <tr key={index} className="skeleton-row">
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-badge"></td>
          <td className="skeleton skeleton-text"></td>
          <td className="skeleton skeleton-text"></td>
        </tr>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              <h3>Đang tải dữ liệu...</h3>
              <p>Vui lòng chờ trong giây lát</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === "success" ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="content-header">
        <div>
          <h2>Quản lý lỗi hệ thống</h2>
          <p>Theo dõi và quản lý các lỗi, cảnh báo trong hệ thống</p>
        </div>
        <button className="btn btn-danger" onClick={handleDeleteAllErrors}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Xóa tất cả lỗi
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card users-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Tổng lỗi</p>
              <p className="stat-card-value">
                {stats.totalErrors.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="stat-card transactions-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Nghiêm trọng</p>
              <p className="stat-card-value">
                {stats.criticalErrors.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="stat-card revenue-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Cảnh báo</p>
              <p className="stat-card-value">
                {stats.warningErrors.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="stat-card active-users-card">
          <div className="stat-card-header">
            <div className="stat-card-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="stat-card-info">
              <p className="stat-card-title">Thông tin</p>
              <p className="stat-card-value">
                {stats.infoErrors.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Errors Table */}
      <div className="data-table">
        <div className="table-header">
          <div className="table-title">
            <h3>Danh sách lỗi</h3>
          </div>
          <div className="table-actions">
            <div className="search-box">
              <input
                type="text"
                placeholder="Tìm kiếm theo nội dung, mã lỗi, vị trí..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="form-input"
              />
              <svg
                className="search-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">Tất cả loại lỗi</option>
              {getUniqueErrorTypes()
                .filter((type) => type !== "all")
                .map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
            </select>
            {(searchText || filter !== "all") && (
              <button onClick={clearAllFilters} className="btn btn-secondary">
                Xóa bộ lọc
              </button>
            )}
          </div>
        </div>

        <div className="table-content">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Thời gian</th>
                <th>Mã lỗi</th>
                <th>Nội dung</th>
                <th>Loại lỗi</th>
                <th>Vị trí</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonLoader />
              ) : currentErrors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state">
                    <div className="empty-icon">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p>Không tìm thấy lỗi nào</p>
                  </td>
                </tr>
              ) : (
                currentErrors.map((error) => (
                  <tr
                    key={error.id}
                    className="table-row-hover"
                    onClick={() => handleErrorClick(error)}
                  >
                    <td>
                      <code className="user-id">{error.id.slice(0, 8)}...</code>
                    </td>
                    <td className="user-date">{formatDate(error.timestamp)}</td>
                    <td>
                      <code className="user-id">
                        {error.errorCode || "N/A"}
                      </code>
                    </td>
                    <td>
                      <div className="user-details">
                        <p className="user-name">
                          {error.message || "Không có nội dung"}
                        </p>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getErrorSeverityClass(
                          error
                        )}`}
                      >
                        {getErrorSeverityText(error)}
                      </span>
                    </td>
                    <td>
                      <div className="user-details">
                        <p className="user-id">
                          {error.errorLocation || "Không xác định"}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div
                        className="action-buttons"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleErrorClick(error)}
                        >
                          Chi tiết
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>

      {/* Error Detail Modal */}
      {showErrorModal && currentError && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-title">
                <div className="user-avatar">
                  {currentError.errorType
                    ? currentError.errorType.charAt(0).toUpperCase()
                    : "E"}
                </div>
                <div>
                  <h3>Chi tiết lỗi hệ thống</h3>
                  <p>{currentError.errorCode || "Mã lỗi không xác định"}</p>
                </div>
              </div>
              <div className="modal-close">
                <span
                  className={`status-badge ${getErrorSeverityClass(
                    currentError
                  )}`}
                >
                  {getErrorSeverityText(currentError)}
                </span>
                <button onClick={closeErrorModal}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>ID lỗi</label>
                  <input
                    type="text"
                    value={currentError.id}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Thời gian</label>
                  <input
                    type="text"
                    value={formatDate(currentError.timestamp)}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Mã lỗi</label>
                  <input
                    type="text"
                    value={currentError.errorCode || "Không có mã lỗi"}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>Vị trí</label>
                  <input
                    type="text"
                    value={currentError.errorLocation || "Không xác định"}
                    disabled
                    className="form-input disabled"
                  />
                </div>
                <div className="form-group">
                  <label>ID người dùng</label>
                  <input
                    type="text"
                    value={currentError.userId || "Không có thông tin"}
                    disabled
                    className="form-input disabled"
                  />
                </div>
              </div>

              <div className="fanpages-section">
                <h5>Nội dung lỗi</h5>
                <div className="table-content">
                  <pre className="payment-json">
                    {currentError.message ||
                      "Không có thông tin chi tiết về lỗi"}
                  </pre>
                </div>
              </div>

              {currentError.stackTrace && (
                <div className="fanpages-section">
                  <h5>Stack Trace</h5>
                  <div className="table-content">
                    <pre className="payment-json">
                      {currentError.stackTrace}
                    </pre>
                  </div>
                </div>
              )}

              <div className="fanpages-section">
                <h5>Dữ liệu chi tiết</h5>
                <div className="table-content">
                  <pre className="payment-json">
                    {JSON.stringify(currentError, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="user-id-display">ID: {currentError.id}</div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={closeErrorModal}>
                  Đóng
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    if (window.confirm("Bạn có chắc chắn muốn xóa lỗi này?")) {
                      handleDeleteError();
                    }
                  }}
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminErrorManagement;
